import React, { CSSProperties } from 'react'

import {
  Body1,
  Box,
  Button,
  ButtonColorVariant,
  ButtonVariant,
  Color,
  Body2,
  Image,
  RadiusScale,
  useTheme,
  Display,
} from '@gousto-internal/citrus-react'

import { MenuApiPreferencesResponse, Recipe } from '@library/menu-service'
import { queueTrackingEvent } from '@library/tracking-integration'

import { useBasketRecipes } from 'hooks/basket/useBasketRecipes'

import { TrackCTAClick } from '../types'
import { InvalidPreferenceTag } from './InvalidPreferenceTag'

const IMAGE_WIDTH = 200

export type SearchRecipeTileProps = {
  addRecipe: (
    recipeId: string,
    view?: string,
    recipeInfo?: { position: string },
    orderId?: string,
    isOrderRecipe?: boolean,
  ) => void
  menuPreferences?: MenuApiPreferencesResponse
  onClick: () => void
  reachedLimit: boolean
  recipe: Recipe
  removeRecipe: (recipeId: string, view?: string, position?: string) => void
  trackingData: {
    invocationId?: string
    menuId: string
    index?: number
  }
}

const SearchRecipeTile = ({
  addRecipe,
  menuPreferences,
  onClick,
  reachedLimit,
  recipe,
  removeRecipe,
  trackingData,
}: SearchRecipeTileProps) => {
  const { theme } = useTheme()
  const recipeImage = recipe.images?.[0]?.crops?.find(({ width }) => width === IMAGE_WIDTH) ?? null

  const { basketHasRecipe } = useBasketRecipes()
  const isInBasket = basketHasRecipe(recipe.coreId)

  const trackingProperties: TrackCTAClick['properties'] = {
    search_invocation_id: trackingData.invocationId,
    search_rank: trackingData.index,
    menu_id: trackingData.menuId,
    add_type: 'search',
    event_screen: 'view_recipe_detail_screen',
    recipe_id: recipe.id,
  }

  const onClickCTA = (event: React.SyntheticEvent) => {
    event.stopPropagation()
    if (isInBasket) {
      removeRecipe(recipe.coreId)
      queueTrackingEvent<TrackCTAClick>({
        action: 'remove_recipe',
        properties: trackingProperties,
      })
    } else {
      addRecipe(recipe.coreId)
      queueTrackingEvent<TrackCTAClick>({
        action: 'add_recipe',
        properties: trackingProperties,
      })
    }
  }

  const onKeyDown = (
    event: React.KeyboardEvent,
    callback: (event: React.KeyboardEvent) => void,
  ) => {
    if (event.code === 'Space' || event.code === 'Enter') {
      event.stopPropagation()
      event.preventDefault()
      callback(event)
    }
  }

  return (
    <Box
      aria-label="View recipe details"
      display={Display.Flex}
      onClick={onClick}
      onKeyDown={(event) => onKeyDown(event, onClick)}
      role="button"
      style={{ cursor: 'pointer' }}
      tabIndex={0}
    >
      <Box borderRadius={1}>
        {recipeImage ? (
          <Image
            alt={recipe.name}
            src={recipeImage.url}
            width="120px"
            height="120px"
            style={{ ...(theme.radius(2 as RadiusScale) as CSSProperties) }}
            data-testid="search-recipe-tile"
          />
        ) : (
          <Box width="120px" height="120px" />
        )}
      </Box>
      <Box flexGrow={1} paddingH={4}>
        <Body1 fontWeight={700} color={Color.ColdGrey_800}>
          {recipe.name}
        </Body1>
        <Box display={Display.Flex}>
          <Body2>{`${recipe.prepTime} mins`}</Body2>
          {(!menuPreferences || menuPreferences?.display_calories) && (
            <Body2>
              &nbsp;&nbsp;|&nbsp;&nbsp;
              {`${recipe.nutritionalInformation.perPortion.energyKcal}kcal`}
            </Body2>
          )}
        </Box>
        {recipe.invalidPreference && (
          <Box paddingV={1}>
            <InvalidPreferenceTag text={recipe.invalidPreference} />
          </Box>
        )}
      </Box>
      <Box>
        <Button
          variant={ButtonVariant.Icon}
          colorVariant={isInBasket ? ButtonColorVariant.Secondary : ButtonColorVariant.Primary}
          icon={isInBasket ? 'remove' : 'add'}
          aria-label={isInBasket ? 'Remove recipe' : 'Add recipe'}
          onClick={onClickCTA}
          onKeyDown={(event) => onKeyDown(event, onClickCTA)}
          tabIndex={0}
          disabled={reachedLimit && !isInBasket}
        />
      </Box>
    </Box>
  )
}

export { SearchRecipeTile }
